/**
 * Shared helper: resolve field definitions from a template_schema object.
 *
 * Modern templates store fields under `template_schema.fields`.
 * Legacy templates (created by the setup wizard before the DynamicForm migration)
 * store them under `template_schema.sections` with a different shape:
 *   { key, type: "richtext", label, required, placeholder }
 *
 * This helper normalises both formats into the standard field shape so every
 * consumer can work uniformly.
 */

interface LegacySection {
  key?: string;
  name?: string;
  label?: string;
  type?: string;
  required?: boolean;
  placeholder?: string;
  help_text?: string;
  rows?: number;
}

type JsonSchemaProperty = {
  title?: string;
  type?: string;
  description?: string;
  format?: string;
  enum?: unknown[];
  enumNames?: string[];
  items?: { enum?: unknown[] };
  readOnly?: boolean;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function resolveSchemaFields(schema: any, uiSchema?: any): any[] {
  if (schema?.fields?.length) return schema.fields;
  if (Array.isArray(schema?.sections)) {
    return schema.sections.map((s: LegacySection) => ({
      field_id: s.key || s.name || s.label,
      label: s.label || s.key || '',
      type: s.type === 'richtext' ? 'textarea' : s.type || 'textarea',
      required: !!s.required,
      help_text: s.placeholder || s.help_text || '',
      rows: s.rows || 6,
    }));
  }
  if (schema?.properties && typeof schema.properties === 'object') {
    const propertyKeys = Object.keys(schema.properties);
    const configuredOrder = Array.isArray(uiSchema?.['ui:order']) ? uiSchema['ui:order'] : null;
    const order = configuredOrder ? (() => {
      const ordered = configuredOrder.reduce<string[]>((result, entry: unknown) => {
        if (entry === '*') {
          propertyKeys.forEach((key) => {
            if (!result.includes(key)) result.push(key);
          });
        } else if (
          typeof entry === 'string'
          && entry in schema.properties
          && !result.includes(entry)
        ) {
          result.push(entry);
        }
        return result;
      }, []);
      return ordered.concat(propertyKeys.filter((key) => !ordered.includes(key)));
    })() : propertyKeys;
    const required = new Set(Array.isArray(schema.required) ? schema.required : []);
    return order.map((fieldId: string) => {
      const property = schema.properties[fieldId] as JsonSchemaProperty;
      const uiEntry = (uiSchema?.[fieldId] || {}) as Record<string, unknown>;
      const widget = String(uiEntry['ui:widget'] || '');
      const type = widget === 'textarea'
        ? 'textarea'
        : widget || (property.format === 'date' ? 'date' : property.type || 'string');
      return {
        field_id: fieldId,
        key: fieldId,
        name: fieldId,
        label: property.title || fieldId,
        type,
        required: required.has(fieldId),
        ...(property.description ? { description: property.description, help_text: property.description } : {}),
        ...(property.enum ? { enum: property.enum } : {}),
        ...(property.enumNames ? { enumNames: property.enumNames } : {}),
        ...(property.items ? { items: property.items } : {}),
        ...(widget ? { ui_widget: widget } : {}),
        ...(uiEntry['ui:mergeFieldKey'] ? { merge_field_key: uiEntry['ui:mergeFieldKey'] } : {}),
        ...((uiEntry['ui:readonly'] === true || uiEntry['ui:readonly'] === 'true' || property.readOnly)
          ? { read_only: true }
          : {}),
        ...(new Set(['display', 'heading', 'subheading', 'paragraph', 'instructions', 'divider', 'list']).has(type)
          ? { display_only: true }
          : {}),
      };
    });
  }
  return [];
}
